import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  Button,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { TYPE_CONFIGS, NAME_MAX_LENGTH } from "../typeConfig";
import TypeTabContent from "./TypeTabContent";
import useTemplateForm from "./useTemplateForm";

export default function TemplateForm() {
  const { t } = useTranslation("storage");
  const classes = useStyles();
  const history = useHistory();
  const form = useTemplateForm();
  const nameEmpty = form.submitted && !form.name?.trim();
  const nameExceedsMax = form.submitted && form.name?.length > NAME_MAX_LENGTH;
  const nameHasError = nameEmpty || nameExceedsMax;
  const nameHelperText = getNameHelperText(nameEmpty, nameExceedsMax, t);
  const pageTitle = getPageTitle(form.isEdit, t);
  const submitBtnText = getSubmitLabel(form.saving, form.isEdit, t);
  const goToIntegrations = () => history.push("/integrations");

  return (
    <>
      <div className={classes.backButton}>
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={goToIntegrations}
        >
          {t("notificationTemplates.form.backToIntegrations")}
        </Button>
      </div>
      <Paper className={classes.root}>
        <Typography variant="h6" className={classes.title}>
          {pageTitle}
        </Typography>
        {form.isLoadingEdit && (
          <Skeleton
            variant="rect"
            height={56}
            className={classes.nameFieldSkeleton}
          />
        )}
        {!form.isLoadingEdit && (
          <TextField
            label={t("notificationTemplates.form.nameLabel")}
            variant="outlined"
            fullWidth
            required
            value={form.name}
            onChange={form.setName}
            error={nameHasError}
            helperText={nameHelperText}
            className={classes.nameField}
          />
        )}
        <Tabs
          value={form.activeTab}
          onChange={(_, v) => form.setActiveTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          className={classes.tabs}
        >
          {TYPE_CONFIGS.map(tc => (
            <Tab
              key={tc.type}
              label={
                <span>
                  {t(`notificationTemplates.type.${tc.formKey}.label`)}
                  {form.isTypeActive(tc.formKey) && (
                    <span className={classes.activeDot} />
                  )}
                </span>
              }
            />
          ))}
        </Tabs>
        <div className={classes.tabPanel}>
          <TypeTabContent
            isEdit={form.isEdit}
            isLoadingEdit={form.isLoadingEdit}
            submitted={form.submitted}
            typeState={form.typeState}
          />
        </div>
        <div className={classes.actions}>
          <Button
            size="small"
            onClick={goToIntegrations}
            disabled={form.saving}
          >
            {t("notificationTemplates.form.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            disableElevation
            onClick={form.handleSave}
            disabled={
              form.saving ||
              form.typeState.loadingDefaults ||
              form.isLoadingEdit ||
              !form.canSubmit
            }
          >
            {submitBtnText}
          </Button>
        </div>
      </Paper>
    </>
  );
}

function getNameHelperText(
  nameEmpty: boolean,
  nameExceedsMax: boolean,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  if (nameEmpty) return t("notificationTemplates.form.required");
  if (nameExceedsMax) {
    return t("notificationTemplates.form.maxLength", { max: NAME_MAX_LENGTH });
  }
  return "";
}

function getPageTitle(isEdit: boolean, t: (key: string) => string): string {
  if (isEdit) return t("notificationTemplates.form.editTitle");
  return t("notificationTemplates.form.createTitle");
}

function getSubmitLabel(
  saving: boolean,
  isEdit: boolean,
  t: (key: string) => string
): string {
  if (saving) return t("notificationTemplates.form.saving");
  if (isEdit) return t("notificationTemplates.form.save");
  return t("notificationTemplates.form.create");
}

const useStyles = makeStyles(theme => ({
  root: {
    padding: 24,
    marginTop: 20,
  },
  backButton: {
    marginBottom: 12,
  },
  title: {
    marginBottom: 24,
  },
  nameField: {
    marginBottom: 24,
    maxWidth: 480,
  },
  nameFieldSkeleton: {
    marginBottom: 24,
    maxWidth: 480,
    borderRadius: 4,
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tabPanel: {
    padding: "20px 0",
    minHeight: 300,
  },
  activeDot: {
    display: "inline-block",
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    marginLeft: 6,
    verticalAlign: "middle",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    paddingTop: 16,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));
